import { useState } from "react";
import styles from "../Shared/CommonFields.module.css";
import { Divider, FormLayout, Collapsible } from "@shopify/polaris";
import { MdOutlineArrowBackIosNew } from "react-icons/md";
import useBundleMixpanelHook from "../../../../hooks/useBundleMixpanelHook";
import { FormCheckbox, FormTextField } from "../../../../components/FormFields";
import IconUploader from "../../../../components/IconUploader/IconUploader";
import useIsMobile from "../../../../hooks/useIsMobile";
import { VariableTextField } from "../../CommonFields/VariableTextField";

type Props_TextFields = {
    bundleType?: string;
};

const TextFields = ({ bundleType = "volume" }: Props_TextFields) => {
    const [expandTextFields, setExpandTextFields] = useState(false);
    const { trackFormSections } = useBundleMixpanelHook();
    const { isMobile } = useIsMobile();

    return (
        <>
            <div
                className={styles.sectionTitleWrapper}
                onClick={() =>
                    setExpandTextFields((ps) => {
                        trackFormSections({
                            cta: "Form Section",
                            ctaType: `Text Fields ${ps ? "Collapsed" : "Expanded"}`,
                            bundleType: bundleType,
                        });
                        return !ps;
                    })
                }
            >
                Text Fields
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        transform: expandTextFields ? "rotate(90deg)" : "rotate(270deg)",
                    }}
                >
                    <MdOutlineArrowBackIosNew size={14} />
                </div>
            </div>
            <Collapsible
                open={expandTextFields}
                id="text-fields-collapsible"
                transition={{
                    duration: "500ms",
                    timingFunction: "ease-in-out",
                }}
                expandOnPrint
            >
                <div style={{ paddingTop: "12px" }}>
                    <FormLayout>
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                                gap: "16px",
                            }}
                        >
                            <FormTextField
                                name="show_unit_price_text"
                                label="Show unit price text"
                                placeholder="/unit"
                                autoComplete="off"
                                maxLength={20}
                                showCharacterCount
                                tooltipContent="Shown only if you've enabled 'Show unit prices' from the Advanced tab in Product pricing settings"
                            />

                            <FormTextField
                                name="free_text"
                                label="Free item label"
                                placeholder="Free"
                                autoComplete="off"
                                maxLength={10}
                                showCharacterCount
                            />
                        </div>

                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                                gap: "16px",
                            }}
                        >
                            <FormTextField
                                name="quantity_text"
                                label="Quantity label"
                                placeholder="Qty"
                                autoComplete="off"
                                maxLength={10}
                                showCharacterCount
                            />

                            <FormTextField
                                name="no_stock_text"
                                label="Out of stock label"
                                placeholder="Out of stock!"
                                autoComplete="off"
                                maxLength={20}
                                showCharacterCount
                            />
                        </div>

                        <Divider borderColor="border" />

                        <VariableTextField
                            name="out_of_stock_disable_text"
                            label="Out of stock Message"
                            placeholder="Oops! Just {itemCount} item(s) are available right now"
                            maxLength={80}
                            showCharacterCount
                            tooltipContent="Shown as a tooltip on the offer when it can't be fully applied due to limited stock"
                            variables={[{ content: "Item count", value: "{itemCount}" }]}
                        />

                        <Divider borderColor="border" />

                        <FormCheckbox
                            name="bundles_design.default_cta"
                            label="Use default CTA"
                            tooltipContent="You can choose to have Shopify theme's default CTA or style your own."
                        />

                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                                gap: "16px",
                            }}
                        >
                            <FormTextField
                                name="add_to_cart_cta_text"
                                label="Add to cart button text"
                                placeholder="Add to Cart"
                                autoComplete="off"
                                maxLength={50}
                                showCharacterCount
                                helpText={"Use {icon} to show icon"}
                            />
                            <IconUploader
                                iconTypeName="add_to_cart_cta_icon_type"
                                iconUrlName="add_to_cart_cta_icon_url"
                                sizeFieldName="bundles_design.add_to_cart_text_size"
                            />
                        </div>
                    </FormLayout>
                </div>
            </Collapsible>
        </>
    );
};

export default TextFields;
